import React from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, ShieldCheck, Truck, Award, ArrowRight } from "lucide-react";
import InfraLogo from "@/components/cima/InfraLogo";

const BANNER_IMG =
  "https://customer-assets.emergentagent.com/job_cima-industrial/artifacts/odihsxhs_image.png";

export default function InfraDistributor() {
  return (
    <section
      id="infra"
      data-testid="infra-section"
      className="relative py-16 lg:py-24 bg-white overflow-hidden"
    >
      {/* Diagonal accent backdrop */}
      <div className="absolute top-0 left-0 w-full h-1 stripe-accent opacity-40" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Image side */}
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="relative">
              <div className="border border-slate-300 p-3 bg-white">
                <div
                  className="aspect-[4/3] w-full bg-cover bg-center grain-overlay"
                  style={{ backgroundImage: `url(${BANNER_IMG})` }}
                />
              </div>

              {/* INFRA logo floating */}
              <div className="absolute -top-6 -left-6 bg-white border border-slate-200 shadow-2xl px-5 py-3 flex items-center">
                <InfraLogo height={48} />
              </div>

              {/* Authorized stamp */}
              <div className="absolute -bottom-5 -right-5 bg-[var(--cima-red)] text-white px-5 py-3 max-w-[260px] shadow-2xl">
                <div className="flex items-center gap-2 mb-1">
                  <BadgeCheck className="w-4 h-4" />
                  <span className="font-mono-cima text-[10px] uppercase tracking-[0.3em]">
                    Autorizado
                  </span>
                </div>
                <div className="font-display font-bold text-base leading-tight">
                  Distribuidor oficial en Zihuatanejo
                </div>
              </div>
            </div>
          </div>

          {/* Copy side */}
          <div className="lg:col-span-6 order-1 lg:order-2">
            <span className="divider-orange mb-5" />
            <p className="font-mono-cima text-xs uppercase tracking-widest text-[var(--cima-red)] mb-3">
              Alianza estratégica
            </p>

            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-[var(--cima-navy)]">
              Somos{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-[#E30613]">distribuidores autorizados</span>
                <span className="absolute inset-x-0 bottom-1 h-2 bg-[var(--cima-orange)]/40 -z-0" />
              </span>{" "}
              de <span className="text-[#E30613]">INFRA</span>.
            </h2>

            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              En <strong className="text-[var(--cima-navy)]">CIMA</strong> trabajamos
              con la calidad, certificación y respaldo técnico de{" "}
              <strong className="text-[#E30613]">INFRA</strong>, la marca líder en gases
              y soluciones industriales en México. Cada producto que distribuimos cumple
              con los más altos estándares de seguridad y trazabilidad de la marca.
            </p>

            {/* INFRA wordmark prominent */}
            <div className="mt-6 inline-flex items-center gap-4 border border-slate-200 bg-white p-4">
              <InfraLogo height={48} />
              <div className="border-l border-slate-200 pl-4">
                <div className="font-mono-cima text-[10px] uppercase tracking-[0.3em] text-[#E30613]">
                  Marca respaldada
                </div>
                <div className="font-display font-semibold text-sm text-[var(--cima-navy)] leading-tight mt-0.5">
                  Calidad nacional desde<br />hace más de 80 años
                </div>
              </div>
            </div>

            {/* Trust pillars */}
            <div className="mt-8 grid sm:grid-cols-3 gap-5">
              {[
                { icon: ShieldCheck, title: "Calidad INFRA", desc: "Productos certificados con respaldo nacional." },
                { icon: Award, title: "Más de 50 años", desc: "Trayectoria local en Zihuatanejo desde 1972." },
                { icon: Truck, title: "Atención directa", desc: "Entrega y soporte técnico en tu negocio." },
              ].map((p, i) => (
                <div
                  key={p.title}
                  data-testid={`infra-pillar-${i}`}
                  className="border-l-2 border-[var(--cima-orange)] pl-3"
                >
                  <p.icon className="w-5 h-5 text-[var(--cima-red)] mb-2" />
                  <div className="font-display font-semibold text-sm text-[var(--cima-navy)]">
                    {p.title}
                  </div>
                  <div className="text-xs text-slate-600 mt-1 leading-tight">{p.desc}</div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/catalogo"
                data-testid="infra-catalog-link"
                className="btn-cima-red px-6 py-3.5 text-sm font-semibold uppercase tracking-wide inline-flex items-center justify-center gap-2"
              >
                Ver catálogo de productos
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/catalogo#medicinal"
                data-testid="infra-medical-link"
                className="btn-cima-ghost px-6 py-3.5 text-sm font-semibold uppercase tracking-wide inline-flex items-center justify-center gap-2"
              >
                Sección médica
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
